import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../shared/material/material.module';
import { WizardStep } from '../models/search-state.models';

/**
 * Componente que exibe um indicador visual de progresso do wizard de busca.
 * 
 * Funcionalidades:
 * - Mostra etapa atual e total de etapas
 * - Permite navegação clicando nas etapas completadas
 * - Usa componentes Material Design
 * - Aplica estilos do tema global
 * 
 * @example
 * <app-search-step-indicator 
 *   [steps]="wizardSteps"
 *   [currentStep]="currentStepIndex"
 *   (stepClick)="onStepClick($event)">
 * </app-search-step-indicator>
 */
@Component({
  selector: 'app-search-step-indicator',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './search-step-indicator.component.html',
  styleUrls: ['./search-step-indicator.component.scss']
})
export class SearchStepIndicatorComponent {
  /**
   * Lista de etapas do wizard
   */
  steps = input.required<WizardStep[]>();

  /**
   * Índice da etapa atual (0-based)
   */
  currentStep = input.required<number>();

  /**
   * Modo compacto para mobile
   */
  compact = input<boolean>(false);

  /**
   * Evento emitido quando o usuário clica em uma etapa
   */
  stepClick = output<number>();

  /**
   * Calcula o progresso percentual do wizard
   */
  progressPercentage = computed(() => {
    const total = this.steps().length;
    const current = this.currentStep();
    return total > 0 ? ((current + 1) / total) * 100 : 0;
  });

  /**
   * Verifica se uma etapa pode ser clicada (navegável)
   * Uma etapa é navegável se:
   * - É a etapa atual
   * - É uma etapa anterior (já visitada)
   * - É a próxima etapa e a atual está completa
   */
  isStepClickable(stepIndex: number): boolean {
    const current = this.currentStep();
    const steps = this.steps();
    
    // Etapa atual sempre é clicável
    if (stepIndex === current) {
      return true;
    }
    
    // Etapas anteriores são clicáveis
    if (stepIndex < current) {
      return true;
    }
    
    // Próxima etapa é clicável se a atual estiver completa
    if (stepIndex === current + 1 && steps[current]?.isComplete) {
      return true;
    }
    
    return false;
  }

  /**
   * Verifica se uma etapa está ativa (é a etapa atual)
   */
  isStepActive(stepIndex: number): boolean {
    return stepIndex === this.currentStep();
  }

  /**
   * Verifica se uma etapa está completa
   */
  isStepComplete(stepIndex: number): boolean {
    return this.steps()[stepIndex]?.isComplete || false;
  }

  /**
   * Verifica se uma etapa foi visitada (está antes da etapa atual)
   */
  isStepVisited(stepIndex: number): boolean {
    return stepIndex < this.currentStep();
  }

  /**
   * Manipula o clique em uma etapa
   */
  onStepClick(stepIndex: number): void {
    if (this.isStepClickable(stepIndex)) {
      this.stepClick.emit(stepIndex);
    }
  }

  /**
   * Retorna a classe CSS para o estado da etapa
   */
  getStepClass(stepIndex: number): string {
    const classes: string[] = [];
    
    if (this.isStepActive(stepIndex)) {
      classes.push('active');
    }
    
    if (this.isStepComplete(stepIndex)) {
      classes.push('complete');
    }
    
    if (this.isStepVisited(stepIndex)) {
      classes.push('visited');
    }
    
    if (this.isStepClickable(stepIndex)) {
      classes.push('clickable');
    }
    
    return classes.join(' ');
  }

  /**
   * Retorna o ícone apropriado para o estado da etapa
   */
  getStepIcon(step: WizardStep, stepIndex: number): string {
    if (this.isStepComplete(stepIndex)) {
      return 'check_circle';
    }
    
    if (this.isStepActive(stepIndex)) {
      return step.icon;
    }
    
    return step.icon;
  }
}
